import { useEffect, useCallback, useRef } from 'react';

const POLL_MS = 5 * 60 * 1000;

/**
 * Fetch machine activity events for the 3D alarm heatmap on mount, when the
 * scene reload token changes, when the browser tab becomes visible again, and
 * every five minutes.
 */
export default function useActivityEvents(machineId, fetchAndSetInitialEvents, options) {
  const opts = options || {};
  const enabled = opts.enabled !== undefined ? opts.enabled : true;
  const refreshToken = opts.refreshToken !== undefined ? opts.refreshToken : null;

  const fetchRef = useRef(fetchAndSetInitialEvents);
  fetchRef.current = fetchAndSetInitialEvents;

  const fetchEvents = useCallback(() => {
    if (!enabled || machineId == null) return;
    fetchRef.current(machineId);
  }, [enabled, machineId]);

  useEffect(() => {
    if (!enabled || machineId == null) return undefined;
    fetchEvents();
    const intervalId = setInterval(fetchEvents, POLL_MS);
    return () => clearInterval(intervalId);
  }, [enabled, machineId, fetchEvents]);

  // Track the previous refreshToken so that when machineId changes (which
  // recreates fetchEvents), this effect does NOT fire a second fetch. Only fire when
  // refreshToken itself genuinely changes.
  const prevTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (!enabled || machineId == null || refreshToken == null) return;
    if (refreshToken === prevTokenRef.current && prevTokenRef.current !== null) return;
    prevTokenRef.current = refreshToken;
    fetchEvents();
  }, [refreshToken, enabled, machineId, fetchEvents]);

  useEffect(() => {
    if (!enabled || machineId == null) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchEvents();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, machineId, fetchEvents]);
}
